/**
 * Your account: the signed-in PERSON, separate from Organisation settings.
 * Shows the sign-in email and how you sign in (email code, Google), and
 * changes the sign-in email:
 *
 *   1. enter the new address → `POST /partner/auth/email-change/start`
 *      (a six-digit code goes to the NEW address);
 *   2. enter the code → `POST /partner/auth/email-change/verify`;
 *   3. the email is changed: the session's tokens are rotated so the portal
 *      shows the new address, other devices were signed out, and the old
 *      address got a security notice.
 *
 * Google is linked by the Google account's id (migration 073), not by an
 * address, so it survives the change; "Unlink Google" clears it (the email
 * code always remains). Organisations, roles and invitations already received
 * belong to the account and move with it.
 *
 * Step-up (security audit 2026-09-28, migration 086): a signed-in session
 * alone no longer moves the account's recovery path. Before the new-address
 * step, and before unlinking Google, the screen asks for a code sent to the
 * CURRENT address (`POST /partner/auth/step-up/start` → `/verify`). A proven
 * step-up lasts 10 minutes for this browser's session, so the screen skips it
 * when `GET /partner/auth/account` says `step_up_fresh`, and asks again if
 * the server answers `STEP_UP_REQUIRED`.
 *
 * Link a Google account (migration 090): while signed in, after the same
 * step-up, "Link a Google account" shows Google's button; the ID token it
 * returns (carrying this session's `google_link_nonce`) goes to
 * `POST /partner/auth/google/link`, which links it by its id whatever its
 * address. For a sign-in email that is not a Google account, the card says
 * so and offers Google's "use your existing email" sign-up, or (owners and
 * admins) inviting the Google address on Team as a separate login.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSession } from '../auth/session';
import { GoogleSignInButton } from '../auth/GoogleSignIn';
import { can } from '../auth/roles';
import { Link } from '../router';
import type { EmailChanged } from '../api/types';
import type { ApiError } from '../api/errors';
import { useUnsavedChangesGuard } from '../router';
import { ErrorNotice, Field, Loading, Modal, PageHead, useToast } from '../ui/kit';
import { useMutation, useQuery } from '../ui/hooks';
import { NotificationPrefs } from '../account/NotificationPrefs';

type Step =
  | { kind: 'idle' }
  | { kind: 'stepup'; challengeId: string; then: StepUpFor }
  | { kind: 'enter' }
  | { kind: 'code'; challengeId: string; newEmail: string };

type StepUpFor = 'email' | 'google' | 'link';

/** Google's sign-up with an existing (non-Gmail) address (support.google.com/accounts/answer/27441). */
export const GOOGLE_SIGNUP_EXISTING_EMAIL_URL = 'https://accounts.google.com/SignUpWithoutGmail';

/** How long the server honours a redeemed step-up code (STEP_UP_VALID_MINUTES). */
const STEP_UP_MS = 10 * 60_000;

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function Account() {
  const { api, reloadIdentity, role } = useSession();
  const toast = useToast();
  const account = useQuery(() => api.getAccount(), ['account']);
  const [step, setStep] = useState<Step>({ kind: 'idle' });
  const [newEmail, setNewEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [pending, setPending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [done, setDone] = useState<(EmailChanged & { previous: string }) | null>(null);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  // "Link a Google account": Google's button is on screen (after the step-up).
  const [linking, setLinking] = useState(false);
  const [linkPending, setLinkPending] = useState(false);
  const [linkError, setLinkError] = useState<ApiError | null>(null);
  const [linkedNotice, setLinkedNotice] = useState(false);
  // A lapsed step-up comes back as 'step_up' rather than an error, so the
  // screen can ask for the code again instead of showing a failure.
  const unlink = useMutation(async () => {
    try {
      return await api.unlinkGoogle();
    } catch (err) {
      if ((err as ApiError).code === 'STEP_UP_REQUIRED') return 'step_up' as const;
      throw err;
    }
  });
  // Until when this session's step-up counts (client-side hint only; the
  // server decides). Seeded from the account read, set on a verified code.
  const [stepUpUntil, setStepUpUntil] = useState(0);
  const [stepUpCode, setStepUpCode] = useState('');
  // The account read's `step_up_fresh`, as a deadline from when it arrived;
  // ignored once the server has said the step-up lapsed.
  const serverFresh = account.data?.step_up_fresh === true;
  const serverFreshUntil = useMemo(() => (serverFresh ? Date.now() + STEP_UP_MS - 30_000 : 0), [serverFresh]);
  const [lapsed, setLapsed] = useState(false);
  const stepUpFresh = () => stepUpUntil > Date.now() || (!lapsed && serverFreshUntil > Date.now());
  const markLapsed = () => {
    setStepUpUntil(0);
    setLapsed(true);
  };

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = window.setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => window.clearTimeout(t);
  }, [cooldown]);

  // A half-finished change (an address typed, or a code on its way) is worth a "discard?" question.
  useUnsavedChangesGuard(step.kind === 'code' || step.kind === 'stepup' || (step.kind === 'enter' && newEmail.trim() !== ''));

  const current = account.data?.email ?? '';
  const cleaned = newEmail.trim().toLowerCase();
  const sameAsNow = cleaned !== '' && cleaned === current.toLowerCase();

  const reset = () => {
    setStep({ kind: 'idle' });
    setNewEmail('');
    setCode('');
    setStepUpCode('');
    setError(null);
  };

  /** Email a code to the CURRENT address, then continue to `then`. */
  const beginStepUp = async (then: StepUpFor) => {
    setPending(true);
    setError(null);
    setDone(null);
    try {
      const res = await api.startStepUp();
      setStep({ kind: 'stepup', challengeId: res.challenge_id, then });
      setStepUpCode('');
      setCooldown(60);
    } catch (err) {
      const e = err as ApiError;
      // A code went out a moment ago: show the form so it can be entered.
      setError(e);
      const retry = e.body?.retryAfterSec;
      if (typeof retry === 'number') setCooldown(retry);
      setStep((s) => (s.kind === 'stepup' ? s : { kind: 'idle' }));
    } finally {
      setPending(false);
    }
  };

  const startChange = () => {
    setDone(null);
    setError(null);
    if (stepUpFresh()) setStep({ kind: 'enter' });
    else void beginStepUp('email');
  };

  const startLink = () => {
    setError(null);
    setLinkError(null);
    setLinkedNotice(false);
    if (stepUpFresh()) setLinking(true);
    else void beginStepUp('link');
  };

  const onGoogleCredential = async (credential: string) => {
    setLinkPending(true);
    setLinkError(null);
    try {
      const res = await api.linkGoogle(credential);
      setLinking(false);
      setLinkedNotice(true);
      account.setData(account.data ? { ...account.data, google_linked: true, google_email: res.google_email } : account.data);
      toast('Google linked');
    } catch (err) {
      if ((err as ApiError).code === 'STEP_UP_REQUIRED') {
        setLinking(false);
        markLapsed();
        void beginStepUp('link');
      } else {
        setLinkError(err as ApiError);
      }
    } finally {
      setLinkPending(false);
    }
  };

  const startUnlink = () => {
    setError(null);
    unlink.reset();
    if (stepUpFresh()) setConfirmUnlink(true);
    else void beginStepUp('google');
  };

  const confirmStepUp = async (e: FormEvent) => {
    e.preventDefault();
    if (step.kind !== 'stepup') return;
    setPending(true);
    setError(null);
    try {
      const res = await api.verifyStepUp(step.challengeId, stepUpCode.trim());
      setStepUpUntil(Date.now() + Math.max(0, res.step_up_valid_for_s * 1000 - 30_000));
      setStepUpCode('');
      if (step.then === 'email') {
        setStep({ kind: 'enter' });
      } else if (step.then === 'link') {
        setStep({ kind: 'idle' });
        setLinking(true);
      } else {
        setStep({ kind: 'idle' });
        setConfirmUnlink(true);
      }
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setPending(false);
    }
  };

  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    setPending(true);
    setError(null);
    try {
      const res = await api.startEmailChange(cleaned);
      setStep({ kind: 'code', challengeId: res.challenge_id, newEmail: cleaned });
      setCode('');
      setCooldown(60);
    } catch (err) {
      if ((err as ApiError).code === 'STEP_UP_REQUIRED') {
        // The step-up lapsed (10 minutes): confirm it's you again, then come back here.
        markLapsed();
        setPending(false);
        await beginStepUp('email');
        return;
      }
      setError(err as ApiError);
      const retry = (err as ApiError).body?.retryAfterSec;
      if (typeof retry === 'number') setCooldown(retry);
    } finally {
      setPending(false);
    }
  };

  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    if (step.kind !== 'code') return;
    setPending(true);
    setError(null);
    try {
      const res = await api.verifyEmailChange(step.challengeId, code.trim());
      const previous = current;
      setDone({ ...res, previous });
      reset();
      account.setData(account.data ? { ...account.data, email: res.email, google_linked: res.google_linked } : account.data);
      // The access token carries the email: rotate it so the rail shows the new one.
      await reloadIdentity().catch(() => undefined);
      toast('Sign-in email changed');
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setPending(false);
    }
  };

  if (account.loading && !account.data) return <Loading label="Loading your account" />;

  return (
    <div>
      <PageHead
        title="Your account"
        lede="How you sign in to the partner portal. This is you, not your organisation: organisation details are under Organisation."
      />
      <ErrorNotice error={account.error} />
      {account.data ? (
        <div className="grid-2" style={{ alignItems: 'start' }}>
          <section className="panel panel-pad stack" aria-labelledby="email-h">
            <div className="stack-sm">
              <h2 id="email-h" className="h-sub">Sign-in email</h2>
              <p style={{ margin: 0, overflowWrap: 'anywhere' }}>
                <strong data-testid="account-email">{current}</strong>
              </p>
              <p className="small muted" style={{ margin: 0 }}>
                Sign-in codes go to this address. Teammates see it on the Team page.
              </p>
            </div>

            {done ? (
              <div className="notice ok" role="status">
                <div className="stack-sm" style={{ gap: 4 }}>
                  <strong>Your sign-in email is now {done.email}.</strong>
                  <span>We sent a notice about the change to {done.previous}.</span>
                  {done.other_sessions_signed_out > 0 ? (
                    <span>
                      For your security we signed you out everywhere else ({done.other_sessions_signed_out}{' '}
                      {done.other_sessions_signed_out === 1 ? 'other sign-in' : 'other sign-ins'}). You stay signed in here.
                    </span>
                  ) : null}
                  {done.google_linked ? (
                    <span>Google stays linked, so “Continue with Google” with the same Google account still signs you in.</span>
                  ) : null}
                </div>
              </div>
            ) : null}

            {step.kind === 'idle' ? (
              <div>
                <button type="button" className="btn" disabled={pending} onClick={startChange}>
                  Change email
                </button>
                <ErrorNotice error={error} />
              </div>
            ) : null}

            {step.kind === 'stepup' ? (
              <form className="stack" onSubmit={confirmStepUp} noValidate aria-label="Confirm it’s you">
                <div className="stack-sm">
                  <h3 className="h-sub" style={{ margin: 0 }}>First, confirm it’s you</h3>
                  <p className="small" style={{ margin: 0 }}>
                    {step.then === 'email' ? 'Changing your sign-in email' : step.then === 'link' ? 'Linking Google' : 'Unlinking Google'} changes how you get back into your
                    account, so we check that you can still read your current email. We sent a code to{' '}
                    <strong style={{ overflowWrap: 'anywhere' }}>{current}</strong>. It works for 10 minutes.
                  </p>
                </div>
                <Field label="Code from your current email">
                  {(p) => (
                    <input
                      {...p}
                      className="input code-input"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      value={stepUpCode}
                      onChange={(e) => setStepUpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      autoFocus
                    />
                  )}
                </Field>
                <ErrorNotice error={error} />
                <div>
                  <button className="btn btn-primary" type="submit" disabled={pending || stepUpCode.length !== 6}>
                    {pending ? 'Checking…' : 'Continue'}
                  </button>
                </div>
                <div className="row-between small" style={{ flexWrap: 'wrap', gap: 6 }}>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={cooldown > 0 || pending}
                    onClick={() => void beginStepUp(step.then)}
                  >
                    {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={reset}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : null}

            {step.kind === 'enter' ? (
              <form className="stack" onSubmit={send} noValidate aria-label="Change sign-in email">
                <Field
                  label="New email"
                  hint="We’ll email a six-digit code to the new address. Nothing changes until you enter it."
                  issues={sameAsNow ? [{ path: 'new_email', message: 'That is already your sign-in email.' }] : []}
                >
                  {(p) => (
                    <input
                      {...p}
                      className="input"
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="you@company.com"
                      autoFocus
                    />
                  )}
                </Field>
                <ErrorNotice error={error} />
                <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-primary" type="submit" disabled={pending || sameAsNow || !EMAIL_SHAPE.test(cleaned) || cooldown > 0}>
                    {pending ? 'Sending…' : cooldown > 0 ? `Send code in ${cooldown}s` : 'Email me a code'}
                  </button>
                  <button type="button" className="btn btn-ghost" onClick={reset}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : null}

            {step.kind === 'code' ? (
              <form className="stack" onSubmit={confirm} noValidate aria-label="Confirm new sign-in email">
                <p className="small" style={{ margin: 0 }}>
                  We sent a code to <strong style={{ overflowWrap: 'anywhere' }}>{step.newEmail}</strong>. It works for 10 minutes.
                </p>
                <Field label="Six-digit code">
                  {(p) => (
                    <input
                      {...p}
                      className="input code-input"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      autoFocus
                    />
                  )}
                </Field>
                <ErrorNotice error={error} />
                <div>
                  <button className="btn btn-primary" type="submit" disabled={pending || code.length !== 6}>
                    {pending ? 'Changing…' : 'Change my email'}
                  </button>
                </div>
                <div className="row-between small" style={{ flexWrap: 'wrap', gap: 6 }}>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      setStep({ kind: 'enter' });
                      setCode('');
                      setError(null);
                    }}
                  >
                    Use a different email
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" disabled={cooldown > 0 || pending} onClick={() => void send()}>
                    {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={reset}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : null}

            <hr className="divider" style={{ margin: '4px 0' }} />
            <div className="stack-sm small muted" data-testid="email-change-explainer">
              <p style={{ margin: 0 }}>
                <strong>What moves with you.</strong> Your organisations, roles, and any team invitations you have already received
                belong to your account, not to the address, so they all move to the new email.
              </p>
              <p style={{ margin: 0 }}>
                <strong>Invitations sent to the new address.</strong> If someone already invited the new address, that invitation
                made a separate account for it, and the change will say the email is in use. Sign in with that address to use the
                invitation, or ask whoever sent it to remove it, then try again.
              </p>
              <p style={{ margin: 0 }}>
                <strong>Security.</strong> Before the change we send a code to your current address to check it’s you. Afterwards
                we email your old address a “This wasn’t me” link that works for 14 days: it puts the old address back and signs
                the account out everywhere. You stay signed in here; every other device is signed out.
              </p>
            </div>
          </section>

          <section className="panel panel-pad stack-sm" aria-labelledby="methods-h">
            <h2 id="methods-h" className="h-sub">How you sign in</h2>
            <ul className="stack-sm" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              <li className="row-between" style={{ gap: 8 }}>
                <span>Email code</span>
                <span className="tag ok">Always on</span>
              </li>
              <li className="row-between" style={{ gap: 8 }}>
                <span>Google</span>
                <span className={`tag ${account.data.google_linked ? 'ok' : ''}`} data-testid="google-status">
                  {account.data.google_linked ? 'Linked' : 'Not linked'}
                </span>
              </li>
            </ul>
            <p className="small muted" style={{ margin: 0 }}>There are no passwords. A code is emailed to you each time.</p>
            {account.data.google_linked ? (
              <>
                <p className="small" style={{ margin: 0 }} data-testid="google-linked-as">
                  {account.data.google_email ? (
                    <>
                      “Continue with Google” as <strong style={{ overflowWrap: 'anywhere' }}>{account.data.google_email}</strong> signs you in.
                    </>
                  ) : (
                    '“Continue with Google” with your linked Google account signs you in.'
                  )}{' '}
                  It stays linked if you change your sign-in email.
                </p>
                {linkedNotice ? (
                  <div className="notice ok" role="status">
                    <span>Google is linked. We emailed {current} about it.</span>
                  </div>
                ) : null}
                <ErrorNotice error={unlink.error} />
                <div>
                  <button type="button" className="btn btn-danger btn-sm" disabled={pending} onClick={startUnlink}>
                    Unlink Google
                  </button>
                </div>
              </>
            ) : account.data.google_signin_available ? (
              <>
                {linking ? (
                  <div className="stack-sm" role="group" aria-labelledby="link-google-h">
                    <h3 id="link-google-h" className="h-sub" style={{ margin: 0 }}>
                      Choose a Google account
                    </h3>
                    <p className="small" style={{ margin: 0 }}>
                      Any Google account you use. Its email can differ from {current}.
                    </p>
                    <GoogleSignInButton
                      nonce={account.data.google_link_nonce ?? undefined}
                      onCredential={(credential) => void onGoogleCredential(credential)}
                      failedText="Google couldn’t load in this browser. Try another browser, or keep using email codes."
                    />
                    {linkPending ? (
                      <p className="small muted" role="status" style={{ margin: 0 }}>
                        Linking…
                      </p>
                    ) : null}
                    <ErrorNotice error={linkError} />
                    <div>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setLinking(false)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <button type="button" className="btn btn-sm" disabled={pending} onClick={startLink}>
                      Link a Google account
                    </button>
                  </div>
                )}
                <p className="small muted" style={{ margin: 0 }} data-testid="google-no-account">
                  No Google account for {current}? Link any Google account you use, or{' '}
                  <a href={GOOGLE_SIGNUP_EXISTING_EMAIL_URL} target="_blank" rel="noopener noreferrer">
                    create one with this email
                  </a>
                  .
                </p>
                {can(role, 'members') ? (
                  <p className="small muted" style={{ margin: 0 }} data-testid="google-team-alternative">
                    Want a separate login instead? <Link to="/team">Invite your Google email to your team</Link>. It gets its own
                    role.
                  </p>
                ) : null}
              </>
            ) : null}
          </section>
        </div>
      ) : null}
      <NotificationPrefs />

      <Modal
        open={confirmUnlink}
        title="Unlink Google?"
        onClose={() => setConfirmUnlink(false)}
        footer={
          <>
            <button type="button" className="btn" autoFocus onClick={() => setConfirmUnlink(false)}>
              Keep it linked
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={unlink.pending}
              onClick={async () => {
                const res = await unlink.run();
                setConfirmUnlink(false);
                if (res === 'step_up') {
                  markLapsed();
                  void beginStepUp('google');
                } else if (res) {
                  account.setData(account.data ? { ...account.data, google_linked: false, google_email: null } : account.data);
                  setLinkedNotice(false);
                  toast('Google unlinked');
                }
              }}
            >
              {unlink.pending ? 'Unlinking…' : 'Unlink Google'}
            </button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          You’ll sign in with an email code sent to {current}. You can link a Google account again here any time.
        </p>
      </Modal>
    </div>
  );
}
